import { ThemeProps } from "./theme";

const isDev = process.env.NODE_ENV !== "production";

async function resizeImageToDataUrl(src: string, size: number, bgColor?: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(src); return; }
      if (bgColor) {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, size, size);
      }
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

async function buildIcons(theme: ThemeProps) {
  if (theme.pwaIcon192) {
    const icons: { src: string; sizes?: string; type?: string; purpose: string }[] = [
      { src: theme.pwaIcon192, sizes: "192x192", type: "image/png", purpose: "any" },
    ];
    if (theme.pwaIcon512) {
      icons.push({ src: theme.pwaIcon512, sizes: "512x512", type: "image/png", purpose: "any" });
    }
    return icons;
  }
  if (theme.logo) {
    const data192 = await resizeImageToDataUrl(theme.logo, 192, theme.color1);
    return [{ src: data192, sizes: "192x192", type: "image/png", purpose: "any" }];
  }
  return null;
}

export function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !(window.navigator as any).standalone;
}

export function isIOSChrome(): boolean {
  return isIOS() && /CriOS/i.test(navigator.userAgent);
}

export async function loadDynamicManifest(theme: ThemeProps): Promise<void> {
  if (!theme.pwaEnabled) {
    if (isDev) console.warn("[pwa] pwaEnabled is false — manifest not injected");
    return;
  }

  const icons = await buildIcons(theme);
  const manifest: Record<string, unknown> = {
    name: theme.name,
    short_name: theme.pwaShortName || theme.name?.substring(0, 12),
    description: `Property search powered by ${theme.name}`,
    start_url: window.location.origin + "/",
    display: "standalone",
    background_color: theme.color2 || "#ffffff",
    theme_color: theme.color1 || "#000000",
  };
  if (icons) manifest.icons = icons;

  const blob = new Blob([JSON.stringify(manifest)], {
    type: "application/manifest+json",
  });
  const manifestUrl = URL.createObjectURL(blob);

  let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  link.href = manifestUrl;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

export function isMobile(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (err) {
    if (isDev) console.warn("[pwa] service worker registration failed:", err);
    return null;
  }
}
